import { Access, Role } from './rbac';
import { BaseEntity } from './base-entity';

export interface User extends BaseEntity {
  firstName: string;
  lastName: string;
  role: Role;
  username: string;
  email: string;
  firstLogin: boolean;
  phone: string;
  password: string;
  locked: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  level: UserLevel;
  redMessages: string[];
  workspaces: UserWorkspace[];
  toolAccess: ToolAccess;
  viewAccess: ViewAccess;
  featureFlags?: PortalAdminFeatureFlags;
  image?: string;
}

export interface UserWorkspace {
  workspace: string; // _id of workspace
  roles: string[]; // _ids of roles
}

export type PermissionWorkspaceMap = { [key: string]: Access };

export interface ToolAccess {
  entityAdder: boolean;
  userManagement: boolean;
  config: boolean;
  indicators: boolean;
  pool: boolean;
  themes: boolean;
  bookmarks: boolean;
  stories: boolean;
  colorsets: boolean;
  importExport: boolean;
  massActions: boolean;
  publishManagement: boolean;
  history: boolean;
}

export interface ToolAccessKey {
  key: keyof ToolAccess; // Ensure curr.key is one of the keys in ToolAccess
  name: string;
  icon: string;
}
export interface ViewAccess {
  atlas: boolean;
  dataBackend: boolean;
  messages: boolean;
}

export interface ViewAccessKey {
  key: keyof ViewAccess; // Ensure curr.key is one of the keys in ToolAccess
  name: string;
  icon: string;
}

/**
 * Opt-in beta features, only ever offered to Portal-Administrator (level 40)
 * users - unlike `ToolAccess`/`ViewAccess`, which restrict access a user
 * would otherwise have, these grant access to functionality that's hidden by
 * default for everyone.
 */
export interface PortalAdminFeatureFlags {
  tracking: boolean;
  accessibility: boolean;
  forecast: boolean;
  gridArrange: boolean;
}

export interface FeatureFlagKey {
  key: keyof PortalAdminFeatureFlags;
  name: string;
  icon: string;
}

export interface UserLevelOption {
  icon: string;
  value: UserLevel;
  label: string;
  suffix?: string;
  suffixIcon?: string;
}

export type UserLevel = 0 | 10 | 20 | 30 | 40;

export const userLevelOptions: UserLevelOption[] = [
  {
    icon: 'fas fa-users-viewfinder',
    value: 0,
    label: 'Nach Arbeitsbereich',
    suffixIcon: 'fas fa-flask',
    suffix: 'beta',
  },
  {
    icon: 'fas fa-eye',
    value: 10,
    label: 'Leserechte',
  },
  {
    icon: 'fas fa-edit',
    value: 20,
    label: 'Bearbeitungsrechte',
  },
  {
    icon: 'fas fa-user-tie',
    value: 30,
    label: 'Administrator:in',
  },
];

export function getUserLevel(value: UserLevel): UserLevelOption {
  return (
    userLevelOptions.find(opt => opt.value === value) ||
    ({
      icon: 'fas fa-user-astronaut',
      value: 40,
      label: 'Portal-Administrator',
    } as UserLevelOption)
  );
}
